// src/components/AccountDetailPage.jsx
// Zoho CRM-style account detail page

import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import useTenantVocabulary, { vocabList } from '../hooks/useTenantVocabulary';
// P0.31 31.A.2 — the type/status ladder, shared with AccountsListPage and held in
// step with the importer by tests/account-status-ladder.test.js.
import { ACCOUNT_TYPES, statusesFor, isLegalStatus } from '../lib/accountLadder';
import { confirmArchiveWithChildren } from '../lib/archiveConfirm';
import './AccountDetailPage.css';
import TechStackTab from './TechStackTab';
import ContactsTab from './ContactsTab';
import OpportunitiesTab from './OpportunitiesTab';
import StrategicSignalsTab from './StrategicSignalsTab';
import TimelineTab from './TimelineTab';
import ActivitiesTab from './ActivitiesTab';
import LeadsTab from './LeadsTab';
import MeetingsTab from './MeetingsTab';

// The account hub's tabs, as DATA. Converted from ~55 lines of hand-written
// buttons on 19 Sep 2026 (P1.5 IA-2a) — each had repeated its own id twice and
// its label once, which is how a tab bar drifts out of step with the switch
// that renders its panels.
//
// Module-level, matching the shape Quinn, Cyrano, Victor, Jarvis, Watchlist,
// Comms and ProfileSettings already use. This page was the last hardcoded one
// and the worst case, being the hub the other tabs hang off.
//
// ORDER IS THE UI ORDER. Seven of these nine shadow a top-level destination,
// which is a deliberate scoped/global pattern rather than duplication — see
// IA-6 before "consolidating" any of them away.
const TABS = [
  { id: 'overview',      label: 'Overview' },
  { id: 'leads',         label: 'Leads' },
  { id: 'opportunities', label: 'Opportunities' },
  { id: 'activities',    label: 'Activities' },
  { id: 'contacts',      label: 'Contacts' },
  { id: 'techstack',     label: 'Tech Stack' },
  { id: 'signals',       label: 'Strategic Signals' },
  { id: 'timeline',      label: 'Timeline' },
  { id: 'meetings',      label: '🎙️ Meetings' },
];

function AccountDetailPage({ accountId, onBack, onNavigateToAccount, onNavigate, openOpportunityId }) {
  const [account, setAccount] = useState(null);

  // The option lists this tenant's own data supports, not one market's vendors.
  // See src/hooks/useTenantVocabulary for why null and [] are kept distinct.
  const tenantVocab = useTenantVocabulary();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(openOpportunityId ? 'opportunities' : 'overview');
  const [showDetails, setShowDetails] = useState(true);
  const [formData, setFormData] = useState({});
  const [queueStatus, setQueueStatus] = useState(null); // null | 'queuing' | 'queued' | 'already_queued' | 'error'
  const [showMoreMenu, setShowMoreMenu]   = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting]           = useState(false);
  const [ziEnriching, setZiEnriching]     = useState(false);
  const [ziEnriched,  setZiEnriched]      = useState(false);

  const fetchAccountDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`/api/accounts/detail?id=${accountId}`);
      const data = await response.json();
      
      if (data.success) {
        setAccount(data.account);
        setFormData({
          accountName: data.account.accountName,
          tier: data.account.tier || '',
          accountStatus: data.account.accountStatus || '',
      accountType: data.account.accountType || 'Customer',
          focusAccount: data.account.focusAccount || false,
          annualRevenue: data.account.annualRevenue || '',
          employeeCount: data.account.employeeCount || '',
          companyWebsite: data.account.companyWebsite || '',
          linkedIn: data.account.linkedIn || '',
          dailyFocus: data.account.dailyFocus || '',
          aiSummary: data.account.aiSummary || '',
          companyOverview: data.account.companyOverview || '',
          strategicInitiatives: data.account.strategicInitiatives || '',
          parentAccountId: data.account.parentCompany?.id || '',
          // Zoho-aligned fields
          industry: data.account.industry || '',
          hqStreet: data.account.hqStreet || '',
          hqCity: data.account.hqCity || '',
          hqState: data.account.hqState || '',
          hqZip: data.account.hqZip || '',
          hqCountry: data.account.hqCountry || '',
          phone: data.account.phone || '',
          rating: data.account.rating || '',
          leadSource: data.account.leadSource || '',
          coreSystem: data.account.coreSystem || '',
          relationshipStrength: data.account.relationshipStrength || '',
          contactCadence: data.account.contactCadence || '',
          monitoring: data.account.monitoring || false,
        });
      } else {
        setError(data.error || 'Failed to load account');
      }
    } catch (err) {
      setError('Failed to fetch account details');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (accountId) {
      fetchAccountDetail();
    }
  }, [accountId, fetchAccountDetail]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /**
   * P0.31 31.A.2 — changing the TYPE can orphan the STATUS.
   *
   * A Customer sitting at "Targeted" that becomes a Competitor now holds a status
   * that type cannot have. Nothing in the database prevents it — only the
   * importer validates — which is how 10 of one tenant's 25 accounts arrived
   * unimportable in October and had to be repaired by hand.
   *
   * The status is CLEARED, not remapped. The app cannot know whether a Customer
   * at "Closed Won" becomes an Active competitor or an Inactive one, and guessing
   * would write a wrong answer silently. An empty select plus the blocked save
   * makes the user decide, which is the only honest option.
   *
   * A status that is still legal under the new type is left alone: Customer
   * "Active" to Competitor "Active" needs no decision.
   */
  const handleTypeChange = (nextType) => {
    setFormData(prev => ({
      ...prev,
      accountType: nextType,
      accountStatus: isLegalStatus(nextType, prev.accountStatus) ? prev.accountStatus : '',
    }));
  };

  const handleSave = async () => {
    // P0.31 31.A.2 — refuse the write the database would accept.
    //
    // `accounts.status` has no CHECK constraint and api/accounts/update.js does
    // not validate the pair, so an illegal combination saves cleanly and only
    // surfaces later, when the importer rejects the row on a round trip. Three
    // accounts in production were already in that state on 24 Oct (2 Vendors and
    // 1 Competitor at "Under Review"). Blocked here rather than remapped, because
    // only the user knows which status they meant.
    if (!isLegalStatus(formData.accountType, formData.accountStatus)) {
      alert(`"${formData.accountStatus}" is not a status a ${formData.accountType} can have. `
          + 'Please choose one from the Account Status list.');
      return;
    }
    try {
      const response = await apiFetch(`/api/accounts/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: accountId,
          ...formData
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAccount(prev => ({ ...prev, ...formData }));
        setIsEditing(false);
        alert('Account updated successfully!');
      } else {
        alert('Failed to update account: ' + data.error);
      }
    } catch (err) {
      alert('Error updating account');
    }
  };

  const handleCancel = () => {
    setFormData({
      accountName: account.accountName,
      tier: account.tier || '',
      accountStatus: account.accountStatus || '',
      accountType: account.accountType || 'Customer',
      focusAccount: account.focusAccount || false,
      annualRevenue: account.annualRevenue || '',
      employeeCount: account.employeeCount || '',
      companyWebsite: account.companyWebsite || '',
      linkedIn: account.linkedIn || '',
      dailyFocus: account.dailyFocus || '',
      aiSummary: account.aiSummary || '',
      companyOverview: account.companyOverview || '',
      strategicInitiatives: account.strategicInitiatives || '',
      parentAccountId: account.parentCompany?.id || '',
      // Zoho-aligned fields
      industry: account.industry || '',
      hqStreet: account.hqStreet || '',
      hqCity: account.hqCity || '',
      hqState: account.hqState || '',
      hqZip: account.hqZip || '',
      hqCountry: account.hqCountry || '',
      phone: account.phone || '',
      rating: account.rating || '',
      leadSource: account.leadSource || '',
      coreSystem: account.coreSystem || '',
      relationshipStrength: account.relationshipStrength || '',
      contactCadence: account.contactCadence || '',
      monitoring: account.monitoring || false,
    });
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="account-detail-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading account...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="account-detail-page">
        <div className="error-state">
          <h2>Error Loading Account</h2>
          <p>{error}</p>
          <button onClick={onBack} className="btn-back">
            ← Back to Accounts
          </button>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="account-detail-page">
        <div className="error-state">
          <h2>Account Not Found</h2>
          <p>This account could not be loaded. It may have been deleted or the ID is invalid.</p>
          <button onClick={onBack} className="btn-back">← Back to Accounts</button>
        </div>
      </div>
    );
  }

  const handleZiEnrich = async () => {
    if (ziEnriching || ziEnriched || !accountId) return;
    setZiEnriching(true);
    try {
      const res  = await apiFetch('/api/accounts/enrich', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId, accountName: account.accountName }),
      });
      const data = await res.json();
      if (data.success) {
        setZiEnriched(true);
        // Refresh account data to show new fields
        // B116 — this was `'/api/accounts/' + accountId`, a route that does not
        // exist: api/accounts/ has no [id].js and vercel.json has no rewrites, so
        // the refresh 404'd every time and the enriched fields never appeared until
        // the user reloaded the page. The detail endpoint takes a query parameter,
        // which is the shape used at :34 of this same file.
        const refreshRes = await apiFetch(`/api/accounts/detail?id=${accountId}`);
        const refreshData = await refreshRes.json();
        if (refreshData.account) setAccount(refreshData.account);
        // Switch to contacts tab if executives were added
        if (data.contactsAdded > 0) setActiveTab('contacts');
      }
    } catch { /* silent */ }
    finally { setZiEnriching(false); }
  };

  const handleQueueForResearch = async () => {
    setQueueStatus('queuing');
    try {
      const res = await apiFetch('/api/research/queue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId, accountName: account.accountName })
      });
      const data = await res.json();
      if (data.success) {
        const result = data.results?.[0];
        setQueueStatus(result?.status === 'already_queued' ? 'already_queued' : 'queued');
      } else {
        setQueueStatus('error');
      }
    } catch {
      setQueueStatus('error');
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const res = await apiFetch('/api/accounts/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: accountId }),
      });
      const data = await res.json();
      if (data.success) {
        onBack && onBack();
      } else if (res.status === 409 && data.canForce) {
        // P0.31 31.B.4 — the account still has contacts or open opportunities, so
        // the server refused rather than leaving them pointing at a row the user
        // can no longer open. Measured in production on 24 Oct: 7 archived
        // accounts already held 22 still-visible contacts.
        if (confirmArchiveWithChildren({
          entity: 'account', name: account.accountName, blockedBy: data.blockedBy,
        })) {
          const forced = await apiFetch('/api/accounts/delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: accountId, force: true }),
          });
          const fdata = await forced.json();
          if (fdata.success) onBack && onBack();
          else alert('Archive failed: ' + (fdata.error || 'unknown error'));
        }
      } else {
        alert('Delete failed: ' + data.error);
        setDeleting(false);
        setShowDeleteModal(false);
      }
    } catch {
      alert('Network error during delete');
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const queueBtnLabel = () => queueStatus === 'queuing' ? '⏳ Queuing…'
    : queueStatus === 'queued' ? '✅ Queued for Tonight'
    : queueStatus === 'already_queued' ? '✅ Already Queued'
    : queueStatus === 'error' ? '❌ Queue Failed'
    : '🌙 Queue for Research';

  return (
    <div className="account-detail-page zoho-style">
      {/* Top Bar */}
      <div className="account-top-bar">
        <div className="top-bar-left">
          <button onClick={onBack} className="back-arrow">←</button>
          <div className="account-icon">
            {account.accountType === 'Internal' ? '🏛️'
              : account.accountType === 'Vendor' ? '🔧'
              : account.accountType === 'Partner' ? '🤝'
              : '🏢'}
          </div>
          <div className="account-title-section">
            <h1 className="account-title">{account.accountName}</h1>
            <div className="account-subtitle">
              {account.accountType && account.accountType !== 'Customer' && (
                <span className={`acct-type-pill acct-type-${account.accountType.toLowerCase()}`}>
                  {account.accountType}
                </span>
              )}
              {account.tier && <span className="tier-pill">{account.tier}</span>}
              <span className="account-status-text">{account.accountStatus}</span>
            </div>
          </div>
        </div>

        <div className="top-bar-right">
          {(!account.accountType || account.accountType === 'Customer' || account.accountType === 'Partner') && (
            <button
              className={`btn-queue${queueStatus === 'queued' || queueStatus === 'already_queued' ? ' btn-queue--done' : ''}${queueStatus === 'error' ? ' btn-queue--error' : ''}`}
              onClick={handleQueueForResearch}
              disabled={queueStatus === 'queuing' || queueStatus === 'queued' || queueStatus === 'already_queued'}
              title="Add to Selma's overnight research queue"
            >
              {queueBtnLabel()}
            </button>
          )}
          <button className="btn-action">📧 Send Email</button>
          {!ziEnriched && !account.zoominfo_id && (
            <button className="btn-zi-enrich" onClick={handleZiEnrich}
              disabled={ziEnriching} title="Enrich account with ZoomInfo">
              {ziEnriching ? '⏳' : '⚡ ZI'}
            </button>
          )}
          {(ziEnriched || account.zoominfo_id) && (
            <span className="btn-zi-enriched" title="ZoomInfo enriched">✓ ZI</span>
          )}
          <button
            className="btn-meeting"
            onClick={() => onNavigate && onNavigate('meeting-from-account', accountId, { accountName: account.accountName })}
            title="Start a recorded meeting session for this account"
          >
            🎙️ Start Meeting
          </button>
          {!isEditing ? (
            <button onClick={() => setIsEditing(true)} className="btn-edit">
              Edit
            </button>
          ) : (
            <>
              <button onClick={handleCancel} className="btn-cancel">
                Cancel
              </button>
              <button onClick={handleSave} className="btn-save">
                Save
              </button>
            </>
          )}
          <div className="more-menu-wrapper" style={{position:'relative'}}>
            <button className="btn-more" onClick={() => setShowMoreMenu(m => !m)}>⋯</button>
            {showMoreMenu && (
              <div className="more-menu-dropdown" style={{
                position:'absolute', top:'calc(100% + 6px)', right:0,
                background:'var(--color-background-primary)',
                border:'0.5px solid var(--color-border-secondary)',
                borderRadius:'var(--border-radius-md)',
                minWidth:'180px', zIndex:100, boxShadow:'none', padding:'4px 0'
              }}>
                <button
                  style={{display:'block', width:'100%', textAlign:'left', padding:'8px 16px',
                    background:'none', border:'none', cursor:'pointer', fontSize:'14px',
                    color:'var(--color-text-danger)'}}
                  onClick={() => { setShowMoreMenu(false); setShowDeleteModal(true); }}
                >
                  Delete account
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs — driven by the TABS array at the top of this file (P1.5 IA-2a).
          Labels and ids used to be hand-written across 55 lines of near-identical
          JSX, each repeating both; the ids are the same set, in the same order. */}
      <div className="account-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Main Content Area */}
      <div className="account-content">
        {activeTab === 'overview' && (
          <>
            {/* Account Information Section */}
            <div className="detail-section">
              <div className="section-header" onClick={() => setShowDetails(!showDetails)}>
                <h3>Account Information</h3>
                <button className="toggle-btn">
                  {showDetails ? 'Hide Details' : 'Show Details'}
                </button>
              </div>
              
              {showDetails && (
                <div className="fields-grid">
                  {/* Row 1 - Account Name | Tier */}
                  <div className="field-row">
                    <div className="field-item">
                      <label>Account Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.accountName}
                          onChange={(e) => handleInputChange('accountName', e.target.value)}
                          className="field-input"
                        />
                      ) : (
                        <div className="field-value">{account.accountName}</div>
                      )}
                    </div>
                    <div className="field-item">
                      <label>Tier</label>
                      {isEditing ? (
                        <select
                          value={formData.tier}
                          onChange={(e) => handleInputChange('tier', e.target.value)}
                          className="field-select"
                        >
                          <option value="">-</option>
                          <option value="Tier 1">Tier 1</option>
                          <option value="Tier 2">Tier 2</option>
                          <option value="Tier 3">Tier 3</option>
                          <option value="Tier 4">Tier 4</option>
                        </select>
                      ) : (
                        <div className="field-value">{account.tier || '-'}</div>
                      )}
                    </div>
                  </div>

                  {/* Row 2 - Focus Account | Account Status */}
                  <div className="field-row">
                    <div className="field-item">
                      <label>Focus Account</label>
                      {isEditing ? (
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.focusAccount || false}
                            onChange={(e) => handleInputChange('focusAccount', e.target.checked)}
                            className="field-checkbox"
                          />
                          <span>Mark as Focus Account</span>
                        </label>
                      ) : (
                        <div className="field-value">
                          <span className={`focus-badge ${account.focusAccount ? 'focus-active' : 'focus-inactive'}`}>
                            {account.focusAccount ? '⭐ Focus Account' : 'Not a Focus Account'}
                          </span>
                        </div>
                      )}
                    </div>
                    {/* P0.31 31.A.1 — THE TYPE WAS WRITABLE BY API AND ABSENT FROM
                        EVERY SCREEN. api/accounts/update.js:31 has always accepted
                        `accountType`; the only accountType control in the app was a
                        FILTER on ContactsPage. So an account misclassified on import
                        could not be corrected anywhere in the UI. */}
                    <div className="field-item">
                      <label>Account Type</label>
                      {isEditing ? (
                        <select
                          value={formData.accountType}
                          onChange={(e) => handleTypeChange(e.target.value)}
                          className="field-select"
                        >
                          {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      ) : (
                        <div className="field-value">{account.accountType || 'Customer'}</div>
                      )}
                    </div>
                    <div className="field-item">
                      <label>Account Status</label>
                      {isEditing ? (
                        <>
                          {/* 31.A.2 — the options follow the TYPE. This list was the
                              eight CUSTOMER statuses, hardcoded, for every account —
                              so a Competitor's form offered "Closed Won" and six
                              others its type cannot hold, and nothing in the database
                              refuses the write. Three accounts in production already
                              held an illegal pair on 24 Oct. */}
                          <select
                            value={formData.accountStatus}
                            onChange={(e) => handleInputChange('accountStatus', e.target.value)}
                            className="field-select"
                          >
                            <option value="">— Select a status —</option>
                            {statusesFor(formData.accountType).map(st =>
                              <option key={st} value={st}>{st}</option>
                            )}
                          </select>
                          {/* Never remapped silently: the app cannot know whether a
                              Customer at "Closed Won" becomes an Active competitor or
                              an Inactive one. The save is blocked until the user says. */}
                          {!isLegalStatus(formData.accountType, formData.accountStatus) && (
                            <p className="field-warning">
                              “{formData.accountStatus}” is not a status a {formData.accountType} can
                              have. Choose one above before saving.
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="field-value">{account.accountStatus}</div>
                      )}
                    </div>
                  </div>

                  {/* Row 3 - Annual Revenue | Employee Count */}
                  <div className="field-row">
                    <div className="field-item">
                      <label>Annual Revenue</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.annualRevenue || ''}
                          onChange={(e) => handleInputChange('annualRevenue', e.target.value)}
                          className="field-input"
                          placeholder="e.g. $1.2B"
                        />
                      ) : (
                        <div className="field-value">
                          {account.annualRevenue
                            ? (() => {
                                // Stored values look like "$300.0M", "$1.2B", "$450M",
                                // or a bare number. Interpret the magnitude suffix as a
                                // multiplier (the old code stripped it, collapsing
                                // "$300.0M" -> 300 -> "$300"), then render the FULL
                                // grouped dollar figure, e.g. $300,000,000.
                                const s = account.annualRevenue.toString().trim();
                                const m = s.match(/([0-9]+(?:\.[0-9]+)?)\s*([kmbt])?/i);
                                if (!m) return account.annualRevenue;
                                const mant = parseFloat(m[1]);
                                if (isNaN(mant)) return account.annualRevenue;
                                const mult = { k: 1e3, m: 1e6, b: 1e9, t: 1e12 }[(m[2] || '').toLowerCase()] || 1;
                                const num = Math.round(mant * mult);
                                return `$${num.toLocaleString('en-US')}`;
                              })()
                            : '-'}
                        </div>
                      )}
                    </div>
                    <div className="field-item">
                      <label>Employee Count</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.employeeCount || ''}
                          onChange={(e) => handleInputChange('employeeCount', e.target.value)}
                          className="field-input"
                          placeholder="e.g. 2,500"
                        />
                      ) : (
                        <div className="field-value">{account.employeeCount || '-'}</div>
                      )}
                    </div>
                  </div>

                  {/* Row 4 - Company Website | LinkedIn */}
                  <div className="field-row">
                    <div className="field-item">
                      <label>Company Website</label>
                      {isEditing ? (
                        <input
                          type="url"
                          value={formData.companyWebsite || ''}
                          onChange={(e) => handleInputChange('companyWebsite', e.target.value)}
                          className="field-input"
                          placeholder="https://www.company.com"
                        />
                      ) : (
                        <div className="field-value">
                          {account.companyWebsite
                            ? <a href={account.companyWebsite} target="_blank" rel="noopener noreferrer" className="field-link">{account.companyWebsite}</a>
                            : '-'}
                        </div>
                      )}
                    </div>
                    <div className="field-item">
                      <label>LinkedIn</label>
                      {isEditing ? (
                        <input
                          type="url"
                          value={formData.linkedIn || ''}
                          onChange={(e) => handleInputChange('linkedIn', e.target.value)}
                          className="field-input"
                          placeholder="https://www.linkedin.com/company/name"
                        />
                      ) : (
                        <div className="field-value">
                          {account.linkedIn
                            ? <a href={account.linkedIn} target="_blank" rel="noopener noreferrer" className="field-link">🔗 {account.linkedIn.replace('https://www.linkedin.com/company/', '').replace(/\/$/, '')}</a>
                            : '-'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Row 4b — Industry | Core System */}
                  <div className="field-row">
                    <div className="field-item">
                      <label>Industry</label>
                      {isEditing ? (
                        <select value={formData.industry || ''} onChange={(e) => handleInputChange('industry', e.target.value)} className="field-select">
                          {/* Industries this tenant actually records. Was a
                              literal list of twelve, nine of them insurance
                              lines — P&C, Life & Annuity, Reinsurance, MGA/MGU,
                              Specialty/E&S, Workers Comp, Mutual, Farm Bureau —
                              offered to every tenant regardless of market. Same
                              rule as the Core System field above.

                              The three kept are relationship shapes rather than
                              industries, and every tenant has them: a consulting
                              firm, a vendor and a partner are what the OTHER
                              party is to you, not what market they sell into.
                              The current value is included even if no other
                              account carries it, or editing an account would
                              silently blank a field it was not there to
                              change. */}
                          <option value="">-</option>
                          {Array.from(new Set([
                            ...vocabList(tenantVocab, 'industry'),
                            ...(formData.industry ? [formData.industry] : []),
                            'Consulting Firm', 'Vendor', 'Partner',
                          ])).map(ind => <option key={ind}>{ind}</option>)}
                        </select>
                      ) : (
                        <div className="field-value">{account.industry || '-'}</div>
                      )}
                    </div>
                    <div className="field-item">
                      <label>Core System</label>
                      {isEditing ? (
                        <select value={formData.coreSystem || ''} onChange={(e) => handleInputChange('coreSystem', e.target.value)} className="field-select">
                          {/* Core systems this tenant actually records. Was a
                              literal list of insurance policy-administration
                              vendors — Guidewire, Duck Creek, Majesco,
                              InsuranceNow, InsurSoft, Sapiens, Origami — offered
                              to every tenant regardless of market.

                              The generic three are kept because they are not a
                              market: every tenant can legitimately say a system
                              is legacy, unknown, or that there are several. The
                              current value is included even if no other account
                              carries it, or editing an account would silently
                              blank a field it was not there to change. */}
                          <option value="">-</option>
                          {Array.from(new Set([
                            ...vocabList(tenantVocab, 'coreSystem'),
                            ...(formData.coreSystem ? [formData.coreSystem] : []),
                            'Legacy / Custom', 'Unknown', 'Multiple',
                          ])).map(cs => <option key={cs}>{cs}</option>)}
                        </select>
                      ) : (
                        <div className="field-value">{account.coreSystem || '-'}</div>
                      )}
                    </div>
                  </div>

                  {/* Address block — Street / City+State / Zip+Country */}
                  <div className="field-row full-width">
                    <div className="field-item">
                      <label>HQ Street</label>
                      {isEditing ? (
                        <input type="text" value={formData.hqStreet || ''} onChange={(e) => handleInputChange('hqStreet', e.target.value)} className="field-input" placeholder="e.g. 1 Tower Square" />
                      ) : (
                        <div className="field-value">{account.hqStreet || '-'}</div>
                      )}
                    </div>
                  </div>
                  <div className="field-row">
                    <div className="field-item">
                      <label>HQ City</label>
                      {isEditing ? (
                        <input type="text" value={formData.hqCity || ''} onChange={(e) => handleInputChange('hqCity', e.target.value)} className="field-input" placeholder="e.g. Philadelphia" />
                      ) : (
                        <div className="field-value">{account.hqCity || '-'}</div>
                      )}
                    </div>
                    <div className="field-item">
                      <label>HQ State</label>
                      {isEditing ? (
                        <input type="text" value={formData.hqState || ''} onChange={(e) => handleInputChange('hqState', e.target.value)} className="field-input" placeholder="e.g. PA" />
                      ) : (
                        <div className="field-value">{account.hqState || '-'}</div>
                      )}
                    </div>
                  </div>
                  <div className="field-row">
                    <div className="field-item">
                      <label>HQ Zip</label>
                      {isEditing ? (
                        <input type="text" value={formData.hqZip || ''} onChange={(e) => handleInputChange('hqZip', e.target.value)} className="field-input" placeholder="e.g. 19103" />
                      ) : (
                        <div className="field-value">{account.hqZip || '-'}</div>
                      )}
                    </div>
                    <div className="field-item">
                      <label>HQ Country</label>
                      {isEditing ? (
                        <input type="text" value={formData.hqCountry || ''} onChange={(e) => handleInputChange('hqCountry', e.target.value)} className="field-input" placeholder="e.g. USA" />
                      ) : (
                        <div className="field-value">{account.hqCountry || '-'}</div>
                      )}
                    </div>
                  </div>

                  {/* Row 4d — Phone | Rating */}
                  <div className="field-row">
                    <div className="field-item">
                      <label>Phone</label>
                      {isEditing ? (
                        <input type="tel" value={formData.phone || ''} onChange={(e) => handleInputChange('phone', e.target.value)} className="field-input" placeholder="e.g. (215) 555-0100" />
                      ) : (
                        <div className="field-value">{account.phone || '-'}</div>
                      )}
                    </div>
                    <div className="field-item">
                      <label>Rating</label>
                      {isEditing ? (
                        <select value={formData.rating || ''} onChange={(e) => handleInputChange('rating', e.target.value)} className="field-select">
                          <option value="">-</option>
                          <option>Hot</option>
                          <option>Warm</option>
                          <option>Cold</option>
                        </select>
                      ) : (
                        <div className="field-value">
                          {account.rating ? (
                            <span className={`rating-pill rating-${(account.rating || '').toLowerCase()}`}>
                              {account.rating === 'Hot' ? '🔴' : account.rating === 'Warm' ? '🟡' : '🔵'} {account.rating}
                            </span>
                          ) : '-'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Row 4e — Relationship Strength | Lead Source */}
                  <div className="field-row">
                    <div className="field-item">
                      <label>Relationship Strength</label>
                      {isEditing ? (
                        <select value={formData.relationshipStrength || ''} onChange={(e) => handleInputChange('relationshipStrength', e.target.value)} className="field-select">
                          <option value="">-</option>
                          <option>Strong — Executive Access</option>
                          <option>Developing — Working Level</option>
                          <option>Weak — Limited Contact</option>
                          <option>None — Net New</option>
                        </select>
                      ) : (
                        <div className="field-value">{account.relationshipStrength || '-'}</div>
                      )}
                    </div>
                    <div className="field-item">
                      <label>Lead Source</label>
                      {isEditing ? (
                        <select value={formData.leadSource || ''} onChange={(e) => handleInputChange('leadSource', e.target.value)} className="field-select">
                          <option value="">-</option>
                          <option>Conference / Event</option>
                          <option>Referral</option>
                          <option>LinkedIn</option>
                          <option>Cold Outreach</option>
                          <option>Inbound</option>
                          <option>Internal</option>
                          <option>Partner</option>
                          <option>Existing Relationship</option>
                          <option>Selma</option>
                        </select>
                      ) : (
                        <div className="field-value">{account.leadSource || '-'}</div>
                      )}
                    </div>
                  </div>

                  {/* Row 4f — Contact Cadence | Monitoring */}
                  <div className="field-row">
                    <div className="field-item">
                      <label>Contact Cadence</label>
                      {isEditing ? (
                        <select value={formData.contactCadence || ''} onChange={(e) => handleInputChange('contactCadence', e.target.value)} className="field-select">
                          <option value="">-</option>
                          <option>Weekly</option>
                          <option>Biweekly</option>
                          <option>Monthly</option>
                          <option>Quarterly</option>
                          <option>As Needed</option>
                        </select>
                      ) : (
                        <div className="field-value">{account.contactCadence || '-'}</div>
                      )}
                    </div>
                    <div className="field-item">
                      <label>Monitoring</label>
                      {isEditing ? (
                        <label className="checkbox-label">
                          <input type="checkbox" checked={formData.monitoring || false}
                            onChange={(e) => handleInputChange('monitoring', e.target.checked)}
                            className="field-checkbox" />
                          <span>Passively tracking — not actively pursuing</span>
                        </label>
                      ) : (
                        <div className="field-value">
                          {account.monitoring
                            ? <span className="monitoring-badge">👁 Monitoring</span>
                            : <span style={{ color: '#94a3b8' }}>Not monitoring</span>}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Row 5 - Corporate Structure */}
                  <div className="field-row full-width">
                    <div className="field-item">
                      <label>Corporate Structure</label>
                      {isEditing ? (
                        <div className="corporate-edit-block">
                          <div className="corporate-edit-field">
                            <span className="corporate-edit-label">Parent Company ID</span>
                            <input
                              type="text"
                              value={formData.parentAccountId || ''}
                              onChange={(e) => handleInputChange('parentAccountId', e.target.value)}
                              className="field-input"
                              placeholder="Enter the account name of the parent company (or leave blank)"
                            />
                            <span className="corporate-edit-hint">
                              Type the exact name of the parent account in Digital Me. Clear the field to remove the relationship.
                            </span>
                          </div>
                          {account.subsidiaries?.length > 0 && (
                            <div className="corporate-edit-note">
                              ℹ️ Subsidiaries ({account.subsidiaries.length}) are managed automatically — edit the parent field on each subsidiary's page.
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="corporate-structure-block">
                          {/* Parent */}
                          {account.parentCompany ? (
                            <div className="corp-row">
                              <span className="corp-role-label corp-parent-label">Parent</span>
                              <button
                                className="corp-account-link"
                                onClick={() => onNavigateToAccount && onNavigateToAccount(account.parentCompany.id)}
                                title={`Open ${account.parentCompany.accountName}`}
                              >
                                🏛️ {account.parentCompany.accountName}
                                {account.parentCompany.tier && (
                                  <span className="corp-tier-badge">{account.parentCompany.tier}</span>
                                )}
                                {account.parentCompany.accountStatus && (
                                  <span className="corp-status-badge">{account.parentCompany.accountStatus}</span>
                                )}
                              </button>
                            </div>
                          ) : (
                            <div className="corp-row">
                              <span className="corp-role-label corp-parent-label">Parent</span>
                              <span className="corp-empty">Independent — no parent set</span>
                            </div>
                          )}

                          {/* Subsidiaries */}
                          {account.subsidiaries?.length > 0 && (
                            <div className="corp-subsidiaries-block">
                              <span className="corp-role-label corp-sub-label">
                                Subsidiaries ({account.subsidiaries.length})
                              </span>
                              <div className="corp-sub-list">
                                {account.subsidiaries.map(sub => (
                                  <button
                                    key={sub.id}
                                    className="corp-account-link corp-sub-link"
                                    onClick={() => onNavigateToAccount && onNavigateToAccount(sub.id)}
                                    title={`Open ${sub.accountName}`}
                                  >
                                    🏢 {sub.accountName}
                                    {sub.tier && (
                                      <span className="corp-tier-badge">{sub.tier}</span>
                                    )}
                                    {sub.accountStatus && (
                                      <span className="corp-status-badge">{sub.accountStatus}</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Row 3 - Company Overview */}
                  <div className="field-row full-width">
                    <div className="field-item">
                      <label>Company Overview</label>
                      {isEditing ? (
                        <textarea
                          value={formData.aiSummary}
                          onChange={(e) => handleInputChange('aiSummary', e.target.value)}
                          className="field-textarea"
                          rows={6}
                        />
                      ) : (
                        <div className="field-value text-content ai-text-block">
                          {(() => {
                            // Use dedicated property if populated, else fall back to aiSummary
                            if (account.companyOverview) {
                              return account.companyOverview.replace(/<br\s*\/?>/gi, '\n').trim();
                            }
                            if (account.aiSummary) {
                              const cleaned = account.aiSummary.replace(/<br\s*\/?>/gi, '\n');
                              const idx = cleaned.search(/Strategic Initiatives?:/i);
                              return (idx > 0 ? cleaned.slice(0, idx) : cleaned).replace(/^Overview:\s*/i, '').trim();
                            }
                            return '-';
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Row 4 - Strategic Initiatives */}
                  <div className="field-row full-width">
                    <div className="field-item">
                      <label>Strategic Initiatives</label>
                      {isEditing ? (
                        <textarea
                          value={formData.strategicInitiatives}
                          onChange={(e) => handleInputChange('strategicInitiatives', e.target.value)}
                          className="field-textarea"
                          rows={4}
                        />
                      ) : (
                        <div className="field-value text-content ai-text-block">
                          {(() => {
                            // Use dedicated property if populated, else fall back to aiSummary
                            if (account.strategicInitiatives) {
                              return account.strategicInitiatives.replace(/<br\s*\/?>/gi, '\n').trim();
                            }
                            if (account.aiSummary) {
                              const cleaned = account.aiSummary.replace(/<br\s*\/?>/gi, '\n');
                              const match = cleaned.match(/Strategic Initiatives?:([\s\S]*)/i);
                              return match ? match[1].trim() : '-';
                            }
                            return '-';
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Row 5 - Account Summary 2x2 */}
                  <div className="field-row full-width">
                    <div className="field-item">
                      <label>Account Summary</label>
                      <div className="stats-grid-2x2">
                        <div className="stat-card stat-leads" onClick={() => setActiveTab('leads')}>
                          <div className="stat-number">{account.leadsCount || 0}</div>
                          <div className="stat-label">🎯 Leads</div>
                        </div>
                        <div className="stat-card stat-opportunities" onClick={() => setActiveTab('opportunities')}>
                          <div className="stat-number">{account.opportunityCount || 0}</div>
                          <div className="stat-label">💼 Opportunities</div>
                        </div>
                        <div className="stat-card stat-contacts" onClick={() => setActiveTab('contacts')}>
                          <div className="stat-number">{account.contactCount || 0}</div>
                          <div className="stat-label">👤 Contacts</div>
                        </div>
                        <div className="stat-card stat-signals" onClick={() => setActiveTab('signals')}>
                          <div className="stat-number">{account.signalCount || 0}</div>
                          <div className="stat-label">⚡ Strategic Signals</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'leads' && (
          <LeadsTab accountId={accountId} />
        )}

        {activeTab === 'opportunities' && (
          <OpportunitiesTab accountId={accountId} accountName={account.accountName} onNavigate={onNavigate} initialOpenOppId={openOpportunityId} />
        )}

        {activeTab === 'activities' && (
          <ActivitiesTab accountId={accountId} accountName={account.accountName} />
        )}

        {activeTab === 'contacts' && (
          <ContactsTab accountId={accountId} accountName={account.accountName} accountType={account.accountType} />
        )}

        {activeTab === 'techstack' && (
          <TechStackTab accountId={accountId} onNavigate={onNavigate} />
        )}

        {activeTab === 'signals' && (
          <StrategicSignalsTab accountId={accountId} accountName={account.accountName} />
        )}

        {activeTab === 'timeline' && (
          <TimelineTab accountId={accountId} accountName={account.accountName} />
        )}

        {activeTab === 'meetings' && (
          <MeetingsTab accountId={accountId} accountName={account.accountName} onNavigate={onNavigate} />
        )}
      </div>

      {showMoreMenu && (
        <div onClick={() => setShowMoreMenu(false)}
          style={{position:'fixed',inset:0,zIndex:99}} />
      )}

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => !deleting && setShowDeleteModal(false)}>
          <div className="modal-panel modal-panel--narrow" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete account?</h2>
            </div>
            <div className="modal-body">
              <p>You are about to permanently archive <strong>{account.accountName}</strong> and all associated records:</p>
              <ul className="delete-warning-list">
                <li>All Contacts</li>
                <li>All Leads</li>
                <li>All Opportunities</li>
                <li>All Strategic Signals</li>
                <li>All Activities</li>
                <li>All Tech Stack entries</li>
                <li>All Meetings</li>
              </ul>
              <p className="delete-confirm-note">This cannot be undone from the app. Are you sure?</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowDeleteModal(false)} disabled={deleting}>Cancel</button>
              <button className="btn-delete-confirm" onClick={handleDeleteAccount} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Yes, delete everything'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AccountDetailPage;
